import React, { useState, useEffect } from 'react';
import { Briefcase, Newspaper, Handshake, Send, Loader2, CheckCircle2, Mail, Paperclip, X, Download, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEPTS = [
  { id: 'sales', icon: Handshake, fallback: 'sales@umapremium.me' },
  { id: 'jobs', icon: Briefcase, fallback: 'jobs@umapremium.me' },
  { id: 'pr', icon: Newspaper, fallback: 'pr@umapremium.me' },
];
const inputCls = 'w-full h-11 rounded-lg bg-slate-900/70 border border-slate-700 text-white text-sm px-3 placeholder:text-slate-500 focus:border-amber-500 focus:outline-none';

const empty = { name: '', email: '', phone: '', company: '', position: '', cv_url: '', subject: '', message: '', website: '' };

// Three department channels (Sales · Careers · Press) with one shared form that routes to the right inbox.
const DepartmentContact = () => {
  const { t, language } = useLanguage();
  const [emails, setEmails] = useState({});
  const [dept, setDept] = useState(null);
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  const [cv, setCv] = useState(null); // {file_id, filename, size} once uploaded
  const [cvBusy, setCvBusy] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/public/departments`).then((r) => r.json()).then((d) => setEmails(Object.fromEntries((d.departments || []).map((x) => [x.id, x.email])))).catch(() => {});
  }, []);

  const open = (id) => { setDept(id); setDone(null); setErr(null); setTimeout(() => document.getElementById('dept-form')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50); };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr(t('departments.cvTooBig')); return; }
    setCvBusy(true); setErr(null);
    try {
      const body = new FormData(); body.append('file', file);
      const r = await fetch(`${API_URL}/api/inquiries/cv`, { method: 'POST', body });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : t('departments.error'));
      setCv(d);
    } catch (ex) { setErr(ex.message); } finally { setCvBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/inquiries`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...f, department: dept, lang: language, cv_file_id: dept === 'jobs' && cv ? cv.file_id : '' }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : t('departments.error'));
      setDone(dept); setF(empty); setCv(null);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  return (
    <div className="mt-16" data-testid="department-contact">
      <div className="text-center mb-8">
        <span className="text-amber-400 font-medium tracking-widest text-sm uppercase">{t('departments.subtitle')}</span>
        <h3 className="text-2xl md:text-3xl font-serif text-white mt-2">{t('departments.title')}</h3>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {DEPTS.map(({ id, icon: Icon, fallback }) => (
          <div key={id} className={`bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border transition-colors ${dept === id ? 'border-amber-500/60' : 'border-slate-700/50 hover:border-amber-500/30'}`} data-testid={`dept-card-${id}`}>
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-amber-400" /></div>
            <h4 className="text-white font-semibold text-lg">{t(`departments.${id}.title`)}</h4>
            <p className="text-slate-400 text-sm mt-1 mb-4 leading-relaxed">{t(`departments.${id}.description`)}</p>
            <a href={`mailto:${emails[id] || fallback}`} className="text-amber-400 hover:text-amber-300 text-sm break-all flex items-center gap-1.5" data-testid={`dept-email-${id}`}><Mail className="w-3.5 h-3.5" /> {emails[id] || fallback}</a>
            {id === 'pr' && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="press-kit-links">
                <a href={`${API_URL}/api/public/press-kit.zip`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-600 hover:border-amber-500 text-slate-200 text-xs transition-colors" data-testid="press-kit-zip"><Download className="w-3.5 h-3.5" /> {t('departments.pressKit')}</a>
                <a href={`${API_URL}/api/public/press-kit/fact-sheet.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-600 hover:border-amber-500 text-slate-200 text-xs transition-colors" data-testid="press-kit-pdf"><FileText className="w-3.5 h-3.5" /> {t('departments.factSheet')}</a>
              </div>
            )}
            <button onClick={() => open(id)} className="mt-4 w-full h-10 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium transition-colors" data-testid={`dept-write-${id}`}>{t('departments.write')}</button>
          </div>
        ))}
      </div>

      {dept && (
        <form id="dept-form" onSubmit={submit} className="mt-8 bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-amber-500/30 max-w-3xl mx-auto" data-testid="dept-form">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <h4 className="text-white font-semibold text-lg">{t(`departments.${dept}.title`)}</h4>
            <div className="flex rounded-lg overflow-hidden border border-slate-700 text-xs ml-auto">
              {DEPTS.map((d) => <button type="button" key={d.id} onClick={() => setDept(d.id)} className={`px-3 h-8 ${dept === d.id ? 'bg-amber-500 text-slate-950 font-semibold' : 'bg-slate-900 text-slate-300'}`} data-testid={`dept-switch-${d.id}`}>{t(`departments.${d.id}.short`)}</button>)}
            </div>
          </div>
          {done ? (
            <div className="text-center py-8" data-testid="dept-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-semibold text-lg">{t('departments.thanks')}</p>
              <p className="text-slate-400 text-sm mt-1">{t(`departments.${done}.reply`)}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <input required value={f.name} onChange={set('name')} placeholder={t('departments.name')} className={inputCls} data-testid="dept-name" />
              <input required type="email" value={f.email} onChange={set('email')} placeholder={t('departments.email')} className={inputCls} data-testid="dept-email" />
              <input value={f.phone} onChange={set('phone')} placeholder={t('departments.phone')} className={inputCls} data-testid="dept-phone" />
              {dept === 'jobs' ? (
                <>
                  <input value={f.position} onChange={set('position')} placeholder={t('departments.position')} className={inputCls} data-testid="dept-position" />
                  <div className="sm:col-span-2 flex flex-wrap items-center gap-3" data-testid="dept-cv-block">
                    {cv ? (
                      <span className="inline-flex items-center gap-2 h-11 px-3 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-sm" data-testid="dept-cv-attached">
                        <Paperclip className="w-4 h-4" /> {cv.filename} <span className="text-emerald-300/70 text-xs">({Math.max(1, Math.round(cv.size / 1024))} KB)</span>
                        <button type="button" onClick={() => setCv(null)} className="text-emerald-300 hover:text-white" aria-label="remove" data-testid="dept-cv-remove"><X className="w-4 h-4" /></button>
                      </span>
                    ) : (
                      <label className={`inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-dashed border-slate-600 hover:border-amber-500 text-slate-300 text-sm cursor-pointer ${cvBusy ? 'opacity-60' : ''}`} data-testid="dept-cv-label">
                        {cvBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {cvBusy ? t('departments.cvUploading') : t('departments.cvAttach')}
                        <input type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={uploadCv} disabled={cvBusy} className="hidden" data-testid="dept-cv-file" />
                      </label>
                    )}
                    <span className="text-xs text-slate-500">{t('departments.cvHint')}</span>
                  </div>
                  <input type="url" value={f.cv_url} onChange={set('cv_url')} placeholder={t('departments.cvUrl')} className={`${inputCls} sm:col-span-2`} data-testid="dept-cv" />
                </>
              ) : (
                <input value={f.company} onChange={set('company')} placeholder={dept === 'pr' ? t('departments.outlet') : t('departments.company')} className={inputCls} data-testid="dept-company" />
              )}
              <input value={f.subject} onChange={set('subject')} placeholder={t('departments.subject')} className={`${inputCls} sm:col-span-2`} data-testid="dept-subject" />
              <textarea required minLength={10} rows={5} value={f.message} onChange={set('message')} placeholder={t(`departments.${dept}.placeholder`)} className={`${inputCls} h-auto py-2.5 sm:col-span-2`} data-testid="dept-message" />
              <input value={f.website} onChange={set('website')} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" name="website" />
              {err && <p className="text-red-300 text-sm sm:col-span-2" data-testid="dept-error">{err}</p>}
              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:opacity-60 text-white px-6 h-11 rounded-lg font-medium transition-colors" data-testid="dept-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t('departments.send')}
                </button>
                <span className="text-xs text-slate-500">{t('departments.privacy')}</span>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default DepartmentContact;
